import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Form, Grid, message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type LocalModelWithPermissionsSpecsFragment } from '../../../api/fragments/LocalModelWithPermissionsSpecs';
import {
    DeleteVehicleDocument,
    type DeleteVehicleMutation,
    type DeleteVehicleMutationVariables,
} from '../../../api/mutations/deleteVehicle';
import {
    UpdateModelDocument,
    type UpdateModelMutation,
    type UpdateModelMutationVariables,
} from '../../../api/mutations/updateModel';
import { type LocalModelSettings } from '../../../api/types';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import AddModelDetails from '../AddVehiclePage/AddModelPage/AddModelDetails';
import useModelValidator from '../AddVehiclePage/AddModelPage/useModelValidator';

export type ModelDetailsPageProps = { model: LocalModelWithPermissionsSpecsFragment };

type FormValues = LocalModelSettings;

const ModelDetailsPage = ({ model }: ModelDetailsPageProps) => {
    const { t } = useTranslation(['model']);
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const validator = useModelValidator();
    const screens = Grid.useBreakpoint();
    const modelId = model.id;
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: model.versioning,
        moduleId: model.moduleId,
    });

    const onSubmit = useHandleError<FormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('model:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateModelMutation, UpdateModelMutationVariables>({
                    mutation: UpdateModelDocument,
                    variables: { id: modelId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('model:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate('/admin/vehicles/models');
        },
        [apolloClient, t, modelId, navigate]
    );

    const deleteModel = useCallback(() => {
        Modal.confirm({
            title: t('model:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('model:deleteModal.content'),
            okText: t('model:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('model:deleteModal.cancelText'),
            async onOk() {
                try {
                    message.loading({
                        content: t('model:message.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with API
                    await apolloClient
                        .mutate<DeleteVehicleMutation, DeleteVehicleMutationVariables>({
                            mutation: DeleteVehicleDocument,
                            variables: { id: modelId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('model:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    navigate('/admin/vehicles/models');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, t, modelId, navigate]);

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['name', 'identifier', 'makeId', 'isActive', 'order', 'bodyType'], model),
        }),
        [model]
    );

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(model.permissions, [permissionKind.updateVehicle]),
            hasPermissions(model.permissions, [permissionKind.deleteVehicle]),
        ],
        [model]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
            {({ handleSubmit }) => (
                <ConsolePageWithHeader
                    extra={
                        hasDeletePermission && (
                            <Button onClick={deleteModel} danger>
                                {t('model:actions.delete')}
                            </Button>
                        )
                    }
                    footer={[
                        hasUpdatePermission && (
                            <Button key="modelUpdate" form="modelUpdate" htmlType="submit" type="primary">
                                {t('model:actions.update')}
                            </Button>
                        ),
                    ]}
                    onBack={() => navigate('/admin/vehicles/models')}
                    title={
                        screens.md
                            ? t('model:title', { name: initialValues.name.defaultValue, id: initialValues.identifier })
                            : `${initialValues.name.defaultValue} ${initialValues.identifier}`
                    }
                >
                    <Form id="modelUpdate" layout="vertical" name="modelUpdate" onSubmitCapture={handleSubmit}>
                        <AddModelDetails disabled={!hasUpdatePermission} offset={offset} updated={updated} />
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default ModelDetailsPage;
